import { useEffect, useState } from "react";
import { useMode } from "../panel/mode";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { fallbackOf } from "../settings/catalog";
import { useFlagOr, useSettings, useT, useText } from "../settings/context";
import {
  SignUpError,
  createAccount,
  isEmailShape,
  isKnownEmail,
} from "../auth/account";
import { SignInError, signIn } from "../auth/session";
import { sendSignInLink } from "../auth/signInLink";
import { useSession } from "../auth/useSession";
import {
  OTP_CODE_LENGTH,
  OtpError,
  isOtpCodeShape,
  normalizeEmail,
  requestOtp,
  verifyOtp,
} from "../auth/otp";
import { useEvent } from "../context/EventProvider";
import { logAction } from "../log/actionLog";
import { useTypologyWord } from "../funnel/wording";
import { NO_EMOJI, hasEmoji } from "../funnel/checks";
import { Button } from "../ui/Button";
import { Prose } from "../ui/Prose";
import { withContext } from "./registry";
import { InPlace } from "../panel/InPlace";

/**
 * THE SIGN-IN SCREEN — configurable like every other, and wired to the real API.
 *
 * `POST /auth/login`. What comes back is a token, kept by `api/client.ts` and
 * carried by every later call.
 *
 * Everything an organiser can say here is a setting: the heading, the sentence
 * above the form, both field labels, the button, and whether the "forgotten
 * password" link is offered at all — some events hand out codes and have no
 * password to forget.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */
export function Login() {
  const title = useText(
    "FO_MEETING_V2_LOGIN_TITLE",
    fallbackOf("FO_MEETING_V2_LOGIN_TITLE"),
  );
  const intro = useText("FO_MEETING_V2_LOGIN_INTRO");
  const html = useText("FO_MEETING_V2_LOGIN_HTML");
  /* Le message est posé AU-DESSUS du formulaire, sauf demande d'une colonne à gauche.
     Toute autre valeur vaut « au-dessus » : c'est la mise en page d'avant le réglage. */
  const htmlAside = useText("FO_MEETING_V2_LOGIN_HTML_SIDE") === "left";
  const emailLabel = useText(
    "FO_MEETING_V2_LOGIN_EMAIL_LABEL",
    fallbackOf("FO_MEETING_V2_LOGIN_EMAIL_LABEL"),
  );
  const passwordLabel = useText(
    "FO_MEETING_V2_LOGIN_PASSWORD_LABEL",
    fallbackOf("FO_MEETING_V2_LOGIN_PASSWORD_LABEL"),
  );
  const submitLabel = useText(
    "FO_MEETING_V2_LOGIN_SUBMIT",
    fallbackOf("FO_MEETING_V2_LOGIN_SUBMIT"),
  );
  /**
   * ═══ LA CONNEXION SANS MOT DE PASSE ═══
   *
   * ⚠️ L'ADRESSE SUFFIT, LE LIEN ARRIVE PAR EMAIL. C'est le mode que la V1 appelle
   * `FO_MEETING_AUTO_CONNECT`, porté par 262 lignes de réglages : la reprise le
   * récupère. Le champ mot de passe disparaît, et le lien « mot de passe oublié » avec
   * lui — il n'y a plus rien à oublier, et le proposer mènerait au même envoi.
   */
  /**
   * ═══ LA CONNEXION FERMÉE ═══
   *
   * Le quatrième mode du sélecteur « Mode de connexion » : l'écran garde son titre et
   * dit que personne ne se connecte, sans formulaire. La barre retire son bouton
   * (voir `Shell`), mais l'adresse `/login` reste tapable, d'où ce texte.
   */
  const disabled = useFlagOr("FO_MEETING_V2_LOGIN_DISABLED");
  const linkOnly = useFlagOr("FO_MEETING_V2_LOGIN_AUTO_CONNECT") && !disabled;
  /**
   * ═══ LA CONNEXION PAR CODE ═══
   *
   * Même promesse que le lien (l'adresse suffit), autre mécanique : un code à six
   * chiffres arrive par email et se tape ICI, sur place. Deux temps sur le même
   * écran, l'adresse puis le code, et pas deux adresses : une navigation perdrait `from`.
   *
   * ⚠️ LE LIEN PASSE DEVANT si un organisateur allume les deux : le réglage du code est
   * déclaré comme n'ayant de sens que sans le lien (`needs` dans le catalogue), et
   * l'écran applique la même règle pour ne pas dépendre du seul panneau.
   */
  const otpMode =
    useFlagOr("FO_MEETING_V2_LOGIN_OTP") && !linkOnly && !disabled;
  const passwordless = linkOnly || otpMode;
  const forgotShown =
    useFlagOr("FO_MEETING_V2_LOGIN_FORGOT_SHOW") && !passwordless;
  const forgotLabel = useText(
    "FO_MEETING_V2_LOGIN_FORGOT_LABEL",
    fallbackOf("FO_MEETING_V2_LOGIN_FORGOT_LABEL"),
  );
  /**
   * ═══ L'ADRESSE D'ABORD — LE PRINCIPE DE LA V1 ═══
   *
   * L'écran VÉRIFIE l'adresse dès sa saisie (`auth/account.ts`), et c'est la réponse
   * qui décide de la suite : reconnue, on demande le mot de passe (ou on envoie le
   * lien, ou le code, selon le mode) ; inconnue, on propose de créer le compte quand
   * l'organisateur l'autorise, sinon on le dit. Quatre temps, un seul formulaire :
   *
   *   email    la saisie de l'adresse, rien d'autre
   *   known    l'adresse a un compte : mot de passe / lien parti / code parti
   *   signup   l'adresse n'en a pas, on crée : prénom, nom, typologie au plus
   *   refused  l'adresse n'en a pas, et la création est éteinte
   *
   * ⚠️ CE QUE CELA RÉVÈLE. `signIn` répond la même chose à « inconnue » et à « mauvais
   * mot de passe », exprès ; vérifier d'abord dit si l'adresse est connue. C'est le
   * principe de la V1, demandé tel quel : sans lui, ni création sur place, ni refus
   * propre. La typologie proposée est celle de l'étape typologie du parcours, avec ses
   * mêmes interrupteurs (`FO_MEETING_V2_TYPO_SHOW_<id>`).
   */
  const signupAllowed = useFlagOr("FO_MEETING_V2_LOGIN_SIGNUP");
  const signupTypo = useFlagOr("FO_MEETING_V2_LOGIN_SIGNUP_TYPO");
  const knownHtml = useText("FO_MEETING_V2_LOGIN_KNOWN_HTML");
  const unknownHtml = useText("FO_MEETING_V2_LOGIN_UNKNOWN_HTML");
  const { value, typos } = useSettings();
  const typologyWord = useTypologyWord();
  const offeredTypos = typos.flatMap((typo) =>
    typo.id !== null && value(`FO_MEETING_V2_TYPO_SHOW_${typo.id}`) !== false
      ? [{ id: typo.id, name: typo.name }]
      : [],
  );

  const t = useT();
  const event = useEvent();
  /**
   * ═══ DEUX FORMULAIRES, UNE PAGE ═══
   *
   * ⚠️ LE LIEN « MOT DE PASSE OUBLIÉ » NE MENAIT NULLE PART. C'était un `<button>` sans
   * `onClick` : réglable, traduisible, affiché, et sans effet. Il bascule maintenant
   * cet écran en demande de sent, sur place plutôt que sur une seconde adresse —
   * une navigation perdrait l'écran d'où la garde nous a fait venir (`from`), qu'on
   * doit retrouver une fois connecté.
   */
  const [asking, setAsking] = useState(false);
  const [sent, setSent] = useState<"none" | "sent" | "no-template">("none");
  /**
   * ═══ UN ENVOI, PUIS ON ATTEND ═══
   *
   * Après un lien ou un code parti, le bouton reste visible mais compte à rebours : un clic par
   * seconde enverrait autant d'emails (et Mailjet les facture), et un participant
   * impatient clique jusqu'à ce que « quelque chose » change. Une minute, et le bouton
   * dit lui-même quand il redevient utile ; changer d'adresse ne remet pas le compteur
   * à zéro, exprès (c'est la parade évidente au délai).
   */
  const RESEND_DELAY = 60;
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  /** Où en est l'adresse : voir « L'ADRESSE D'ABORD » plus haut. */
  const [stage, setStage] = useState<"email" | "known" | "signup" | "refused">(
    "email",
  );
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  /**
   * ⚠️ UN ÉMOJI DANS LE PRÉNOM OU LE NOM SE DIT SOUS LE CHAMP, AVANT D'ENVOYER (bug #508).
   * Ces colonnes ne savent pas le garder : api-v1 échouait sur le compte après avoir écrit
   * l'authentification, et l'écran annonçait « Cette adresse a déjà un compte ».
   */
  const [emojiIn, setEmojiIn] = useState({ firstName: false, lastName: false });
  /** L'identifiant choisi dans la liste, ou `""` tant que rien n'est choisi. */
  const [typoId, setTypoId] = useState("");
  /** Mode code : `false` tant qu'on saisit l'adresse, `true` quand on attend le code. */
  const [codeStage, setCodeStage] = useState(false);
  const [code, setCode] = useState("");
  /** Mode code : l'email vient de (re)partir, ou il faut en redemander un. */
  const [otpNotice, setOtpNotice] = useState<
    "none" | "sent" | "resent" | "no-template" | "expired"
  >("none");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  // Where the guard turned them away from. Back there once they are in — being
  // sent to the home screen after signing in loses whatever they had clicked.
  const { state } = useLocation();
  const { context } = useParams();
  /**
   * ⚠️ ET L'ACCUEIL DE *CET* ÉVÉNEMENT, PAS LA RACINE NUE. Le repli valait `"/"`, une
   * adresse qui ne désigne aucun événement : quiconque ouvrait `/mon-evenement/login`
   * DIRECTEMENT — un signet, un lien d'e-mail vers la connexion, une adresse recopiée —
   * n'avait pas été redirigé par la garde, donc ne portait pas de `from`, et se
   * retrouvait après une connexion RÉUSSIE devant « Il manque l'événement dans
   * l'adresse ». Le contexte est dans l'URL qu'on est en train de lire ; il suffit de le
   * garder.
   */
  const intended =
    typeof (state as { from?: unknown })?.from === "string"
      ? (state as { from: string }).from
      : withContext("/", context);

  /**
   * ⚠️ ON DIT POURQUOI ON EST LÀ. Un jeton périmé renvoie ici (voir `Shell.tsx`) : sans
   * un mot, quelqu'un qui vient de cliquer sur le lien de son e-mail atterrit sur un
   * formulaire de connexion et croit s'être trompé de lien — ou pire, que l'événement ne
   * le connaît plus. La phrase ne coûte rien et répond à la seule question qu'il se pose.
   */
  const expiree = (state as { expiree?: unknown })?.expiree === true;
  /**
   * ⚠️ DÉJÀ CONNECTÉ ET ARRIVÉ ICI DIRECTEMENT : RIEN À FAIRE SUR CET ÉCRAN. Un lien
   * d'auto-connexion (`…/login?token=…`, adopté avant le premier rendu par
   * `adoptTokenFromUrl`) ou un signet mène ici avec une session valide ; montrer un
   * formulaire de connexion à quelqu'un qui vient de se connecter fait croire que le
   * lien n'a pas marché. On file à l'accueil de l'événement.
   *
   * SEULEMENT SANS `from` : la garde, elle, renvoie ici AVEC `from`, et au moment où
   * elle le fait le jeton refusé est encore en place (elle le jette dans un effet, juste
   * après). Rediriger dans ce cas repartirait vers la page gardée, qui renverrait ici.
   */
  const signedIn = useSession();
  const arrivedDirectly = !(state as { from?: unknown })?.from;
  const mode = useMode();

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      logAction(event, "signIn", { label: "mot de passe" });
      navigate(intended, { replace: true });
    } catch (cause) {
      /* Le message de `SignInError` est le français d'`auth/session.ts` : l'écran,
         qui connaît la langue lue, le redit sous sa clé. Seul `refused` les sépare. */
      setError(
        cause instanceof SignInError
          ? cause.refused
            ? t("SIGN_IN_FAILED", "Email ou mot de passe incorrect.")
            : t(
                "SIGN_IN_UNAVAILABLE",
                "La connexion est momentanément indisponible. Réessayez dans un instant.",
              )
          : t("SOMETHING_WENT_WRONG", "Une erreur est survenue."),
      );
    } finally {
      setBusy(false);
    }
  };

  const askForLink = async () => {
    if (busy || !event || cooldown > 0) return;
    setBusy(true);
    setError(null);
    setSent("none");
    try {
      const { sent: ok } = await sendSignInLink(
        event.domainId,
        event.eventId,
        email,
      );
      setSent(ok ? "sent" : "no-template");
      if (ok) setCooldown(RESEND_DELAY);
    } catch {
      setError(
        t(
          "PASSWORD_RESET_FAILED",
          "La demande n'a pas pu être envoyée. Réessayez dans un instant.",
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  const backToSignIn = () => {
    setAsking(false);
    setSent("none");
    setError(null);
  };

  /**
   * ⚠️ L'ÉCRAN DE DEMANDE S'OUVRE PROPRE (bug #405). Après un mauvais mot de passe,
   * « Email ou mot de passe incorrect » restait affiché sur « Mot de passe oublié »,
   * où rien n'a encore été envoyé, et son `role="alert"` le faisait relire. Même
   * remise à zéro que `backToSignIn`, dans l'autre sens.
   */
  const openForgot = () => {
    setAsking(true);
    setSent("none");
    setError(null);
  };

  /**
   * Demande (ou redemande) un code. Le serveur remplace le précédent : redemander
   * n'a pas d'autre coût qu'un email, et c'est la seule issue d'un code périmé.
   */
  const askForCode = async (again = false) => {
    if (busy || !event || cooldown > 0) return;
    setBusy(true);
    setError(null);
    setOtpNotice("none");
    try {
      const { sent: ok } = await requestOtp(
        event.domainId,
        event.eventId,
        email,
      );
      if (ok) {
        setCode("");
        setCodeStage(true);
        setOtpNotice(again ? "resent" : "sent");
        setCooldown(RESEND_DELAY);
      } else {
        setOtpNotice("no-template");
      }
    } catch {
      setError(
        t(
          "PASSWORD_RESET_FAILED",
          "La demande n'a pas pu être envoyée. Réessayez dans un instant.",
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (typed = code) => {
    if (busy || !event) return;
    if (!isOtpCodeShape(typed)) {
      setError(t("OTP_CODE_SHAPE", "Le code se compose de six chiffres."));
      return;
    }
    setBusy(true);
    setError(null);
    setOtpNotice("none");
    try {
      await verifyOtp(event.domainId, email, typed);
      logAction(event, "signIn", { label: "code" });
      navigate(intended, { replace: true });
    } catch (cause) {
      const refusal = cause instanceof OtpError ? cause.refusal : "unavailable";
      if (refusal === "wrong") {
        setError(
          t(
            "OTP_CODE_WRONG",
            "Ce code n'est pas le bon. Vérifiez-le et réessayez.",
          ),
        );
      } else if (refusal === "expired") {
        /* Plus rien à retaper : la case se vide et l'issue est de redemander. On
           l'annonce en `status`, avec le bouton juste dessous, plutôt qu'en erreur. */
        setCode("");
        setOtpNotice("expired");
      } else {
        setError(t("SOMETHING_WENT_WRONG", "Une erreur est survenue."));
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * ⚠️ LE SIXIÈME CHIFFRE VALIDE SANS BOUTON. Sur un téléphone, le code se recopie depuis
   * la notification et la proposition du clavier (`one-time-code`) le pose en entier :
   * demander ensuite d'appuyer sur « Valider » est un geste de plus pour rien. Le bouton
   * reste, pour qui tape à la main et s'arrête, ou pour un lecteur d'écran.
   */
  const onCodeChange = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, OTP_CODE_LENGTH);
    setCode(digits);
    if (error) setError(null);
    if (digits.length === OTP_CODE_LENGTH) void submitCode(digits);
  };

  /**
   * Revenir à l'adresse, d'où qu'on soit : le mot de passe tapé, le code attendu, les
   * champs de création et ce qui a été dit s'effacent avec elle. La V1 fait pareil
   * (`form.resetFields()`), en plus brutal.
   */
  const changeAddress = () => {
    setStage("email");
    setPassword("");
    setEmojiIn({ firstName: false, lastName: false });
    setCodeStage(false);
    setCode("");
    setOtpNotice("none");
    setSent("none");
    setError(null);
  };

  /**
   * LE PREMIER TEMPS : la forme de l'adresse, puis le serveur. Reconnue, le mode
   * décide : le mot de passe s'affiche, ou le lien (ou le code) part sans second clic,
   * comme la V1 qui soumet d'elle-même l'étape suivante en `autoConnect`.
   */
  const checkAddress = async () => {
    if (busy || !event) return;
    if (!isEmailShape(email)) {
      setError(
        t(
          "EMAIL_FORMAT_INVALID",
          "Le format de l'adresse email est incorrect.",
        ),
      );
      return;
    }
    setBusy(true);
    setError(null);
    let known: boolean;
    try {
      known = await isKnownEmail(event.domainId, email);
    } catch {
      setError(
        t(
          "EMAIL_CHECK_FAILED",
          "L'adresse n'a pas pu être vérifiée. Réessayez dans un instant.",
        ),
      );
      return;
    } finally {
      setBusy(false);
    }
    if (!known) {
      setStage(signupAllowed ? "signup" : "refused");
      return;
    }
    setStage("known");
    if (linkOnly) return askForLink();
    if (otpMode) return askForCode();
  };

  /**
   * LA CRÉATION DU COMPTE, comme la V1 : prénom, nom, la typologie si l'événement la
   * demande, PAS de mot de passe. Le serveur rend un jeton, la session s'ouvre, et on
   * repart vers la page visée comme après une connexion.
   */
  const signUp = async () => {
    if (busy || !event) return;
    if (firstName.trim() === "" || lastName.trim() === "") {
      setError(t("NAMES_REQUIRED", "Indiquez votre prénom et votre nom."));
      return;
    }
    const withEmoji = {
      firstName: hasEmoji(firstName),
      lastName: hasEmoji(lastName),
    };
    if (withEmoji.firstName || withEmoji.lastName) {
      setEmojiIn(withEmoji);
      setError(null);
      return;
    }
    if (signupTypo && offeredTypos.length > 0 && typoId === "") {
      setError(t("CHOOSE_ONE_OPTION", "Choisissez une option dans la liste."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await createAccount(event.domainId, event.eventId, {
        email,
        firstName,
        lastName,
        typoId: typoId ? Number(typoId) : null,
      });
      logAction(event, "accountCreation");
      navigate(intended, { replace: true });
    } catch (cause) {
      /* Prise entre-temps (ou par une autre casse) : on revient à l'adresse et on le
         dit, plutôt que de laisser un formulaire de création qui ne peut plus aboutir. */
      if (cause instanceof SignUpError && cause.refusal === "taken") {
        setStage("email");
        setError(
          t(
            "EMAIL_ALREADY_USED",
            "Cette adresse a déjà un compte. Connectez-vous avec.",
          ),
        );
      } else if (cause instanceof SignUpError && cause.refusal === "emoji") {
        setError(t("EMOJI_NOT_ALLOWED", NO_EMOJI));
      } else {
        setError(
          t(
            "SIGN_UP_FAILED",
            "Le compte n'a pas pu être créé. Réessayez dans un instant.",
          ),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    /* « Mot de passe oublié » et le second temps du code ont leur propre chemin. */
    if (asking) return askForLink();
    if (otpMode && codeStage) return submitCode();
    /* Refusée, on revérifie : l'adresse a pu être corrigée entre-temps. */
    if (stage === "email" || stage === "refused") return checkAddress();
    if (stage === "signup") return signUp();
    if (linkOnly) return askForLink();
    if (otpMode) return askForCode();
    return submit();
  };

  /* L'ORGANISATEUR vient ici EXPRÈS, par le sélecteur d'écran du panneau, pour régler
     ce que l'écran dit (mode de connexion, email du code…) : le renvoyer parce qu'il
     est connecté en ferait le seul écran impossible à configurer (voir `panel/mode.ts`). */
  if (signedIn && arrivedDirectly && !expiree && mode !== "organiser") {
    return <Navigate to={intended} replace />;
  }

  return (
    <section className="page">
      <div className="ttl" data-cfg="loginTitle">
        {asking ? (
          <h2>{t("PASSWORD_RESET_TITLE", "Mot de passe oublié")}</h2>
        ) : (
          <InPlace settingKey="FO_MEETING_V2_LOGIN_TITLE" text={title} as="h2" />
        )}
        {asking ? (
          <p>
            {t(
              "PASSWORD_RESET_INTRO",
              "Indiquez votre adresse : nous vous envoyons de quoi vous reconnecter.",
            )}
          </p>
        ) : (
          <>
            <InPlace settingKey="FO_MEETING_V2_LOGIN_INTRO" text={intro} as="p" />
            {/* En mode « lien seul », on dit d'emblée ce qui va se passer : un
                formulaire à une case et un bouton « Se connecter » laisse croire
                qu'on va entrer tout de suite. */}
            {linkOnly && (
              <p>
                {t(
                  "SIGN_IN_LINK_INTRO",
                  "Indiquez votre adresse : vous recevez un lien qui vous connecte, sans mot de passe.",
                )}
              </p>
            )}
            {otpMode && !codeStage && (
              <p>
                {t(
                  "OTP_INTRO",
                  "Indiquez votre adresse : vous recevez un code à six chiffres, à saisir ici. Pas de mot de passe.",
                )}
              </p>
            )}
            {otpMode && codeStage && (
              <p>
                {t(
                  "OTP_CODE_INTRO",
                  "Un code vient de partir à $1. Saisissez-le ci-dessous : il vaut cinq minutes.",
                  /* Telle que le serveur l'a reçue, pas telle qu'elle a été tapée. */
                  [normalizeEmail(email)],
                )}
              </p>
            )}
          </>
        )}
      </div>

      {/**
       * ═══ LE BLOC LIBRE DE L'ORGANISATEUR, ET OÙ IL SE PLACE ═══
       *
       * Son HTML, assaini comme partout (`ui/Prose`), sous le titre : au-dessus du
       * formulaire, ou dans une colonne À SA GAUCHE quand il l'a demandé
       * (`FO_MEETING_V2_LOGIN_HTML_SIDE`). Pas sur l'écran « mot de passe oublié »,
       * qui n'est pas la page qu'il a écrite. Vide, rien ne s'affiche.
       *
       * ⚠️ LE CADRE EST TOUJOURS POSÉ, même sans message, même au-dessus : c'est UNE
       * structure, dont seule la classe change. Deux arbres JSX selon le réglage
       * auraient fait remonter un formulaire neuf, saisie perdue, au moment où
       * l'organisateur essaie l'autre place depuis le panneau. Sans modificateur, le
       * cadre et sa colonne sont deux piles comme la page elle-même (`chassis.css`) ;
       * `--side` ne devient une grille qu'à partir de 720 px (`desktop.css`), sur un
       * téléphone le message repasse au-dessus.
       */}
      <div
        className={
          htmlAside && !asking && html.trim() !== ""
            ? "login-layout login-layout--side"
            : "login-layout"
        }
      >
        {!asking && html.trim() !== "" && (
          <Prose html={html} className="se-prose" data-cfg="loginBlock" />
        )}

        <div className="login-layout__main">
          {/* Avant le formulaire, et pas après : c'est le contexte de ce qu'on s'apprête à
          faire. `role="status"` et non `alert` — rien n'a échoué, on informe. */}
          {expiree && !asking && (
            <p className="hint" role="status">
              {t(
                "SESSION_EXPIRED",
                "Votre session a expiré. Reconnectez-vous pour reprendre où vous en étiez.",
              )}
            </p>
          )}

          {/* Connexion fermée : le message tient lieu de formulaire. */}
          {disabled && (
            <p className="hint" role="status">
              {t(
                "SIGN_IN_CLOSED",
                "La connexion n'est pas ouverte pour cet événement.",
              )}
            </p>
          )}

          {/* ⚠️ EN MODE « LIEN SEUL », LE FORMULAIRE PRINCIPAL EST DÉJÀ LA DEMANDE. Il n'y
          a pas de mot de passe à saisir, donc rien à basculer : ce que la bascule
          « mot de passe oublié » ouvre est exactement ce qu'on affiche d'emblée. */}
          {!disabled && (
            <form className="form" onSubmit={onSubmit} noValidate>
              {/* ⚠️ EN ATTENDANT LE CODE, L'ADRESSE NE SE RETAPE PAS, ELLE SE RELIT. Elle est
            rappelée dans la phrase d'intro ; la case disparaît pour que le clavier
            numérique et l'autocomplétion `one-time-code` visent la seule case restante. */}
              {!(otpMode && codeStage) && (
                <div className="f f--wide" data-cfg="loginFields">
                  <label htmlFor="login-email">{emailLabel}</label>
                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    /* `username` rather than `email`: it is what password managers look
                 for, and a form they cannot read is a form people mistype. */
                    autoComplete="username"
                    required
                    /* Jugée, l'adresse se fige (la V1 fait pareil) : ce qui suit vaut
                       pour ELLE. Le lien juste dessous permet d'en changer. */
                    disabled={stage === "known" || stage === "signup"}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      /* Refusée puis corrigée : on redevient une saisie ordinaire. */
                      if (stage === "refused") setStage("email");
                      if (error) setError(null);
                    }}
                  />
                </div>
              )}

              {!asking && (stage === "known" || stage === "signup") && (
                <div className="f--wide acts">
                  <button
                    type="button"
                    className="lnk"
                    disabled={busy}
                    onClick={changeAddress}
                  >
                    {t("OTP_CHANGE_EMAIL", "Changer d'adresse")}
                  </button>
                </div>
              )}

              {/* Ce que l'organisateur dit à une adresse reconnue, avant la suite. */}
              {!asking && stage === "known" && knownHtml.trim() !== "" && (
                <Prose
                  html={knownHtml}
                  className="se-prose f--wide"
                  data-cfg="loginFields"
                />
              )}

              {otpMode && codeStage && (
                <div className="f f--wide" data-cfg="loginFields">
                  <label htmlFor="login-code">
                    {t("OTP_CODE_LABEL", "Code reçu par email")}
                  </label>
                  <input
                    id="login-code"
                    type="text"
                    name="code"
                    /* `one-time-code` : iOS et Android proposent le code lu dans le
                 email ; `numeric` ouvre le pavé de chiffres sans interdire de coller. */
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={OTP_CODE_LENGTH}
                    required
                    autoFocus
                    value={code}
                    onChange={(e) => onCodeChange(e.target.value)}
                  />
                </div>
              )}

              {/* ⚠️ LE CHAMP DE MOT DE PASSE DISPARAÎT, IL NE SE GRISE PAS. Laissé là, un
            gestionnaire de mots de passe continue de le remplir et l'écran demande une
            chose dont il n'a que faire. L'adresse, elle, reste : c'est la seule qu'on
            vient de saisir, et la redemander serait la faire retaper. */}
              {!asking && !passwordless && stage === "known" && (
                <div className="f f--wide" data-cfg="loginFields">
                  <label htmlFor="login-password">{passwordLabel}</label>
                  <input
                    id="login-password"
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    required
                    /* Il vient d'apparaître, et c'est lui qu'on attend. */
                    autoFocus
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              )}

              {/**
               * ═══ L'ADRESSE N'A PAS DE COMPTE ═══
               *
               * Création autorisée : le message de l'organisateur, puis prénom, nom et,
               * si l'événement le demande, la typologie — les champs de la V1, pas un
               * de plus. Refusée : le même message tient lieu de réponse, ou la phrase
               * du produit, en `alert` : c'est bien un refus.
               */}
              {!asking && stage === "signup" && (
                <>
                  {unknownHtml.trim() !== "" && (
                    <Prose
                      html={unknownHtml}
                      className="se-prose f--wide"
                      data-cfg="loginSignup"
                    />
                  )}
                  <div className="f f--moyen" data-cfg="loginSignup">
                    <label htmlFor="login-first-name">
                      {t("FIRST_NAME", "Prénom")}
                    </label>
                    <input
                      id="login-first-name"
                      type="text"
                      name="firstName"
                      autoComplete="given-name"
                      required
                      autoFocus
                      aria-invalid={emojiIn.firstName || undefined}
                      aria-describedby={
                        emojiIn.firstName ? "login-first-name-err" : undefined
                      }
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        setEmojiIn((was) => ({ ...was, firstName: false }));
                      }}
                    />
                    {emojiIn.firstName && (
                      <p
                        className="f__err"
                        id="login-first-name-err"
                        role="alert"
                      >
                        {t("EMOJI_NOT_ALLOWED", NO_EMOJI)}
                      </p>
                    )}
                  </div>
                  <div className="f f--moyen" data-cfg="loginSignup">
                    <label htmlFor="login-last-name">
                      {t("LAST_NAME", "Nom")}
                    </label>
                    <input
                      id="login-last-name"
                      type="text"
                      name="lastName"
                      autoComplete="family-name"
                      required
                      aria-invalid={emojiIn.lastName || undefined}
                      aria-describedby={
                        emojiIn.lastName ? "login-last-name-err" : undefined
                      }
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        setEmojiIn((was) => ({ ...was, lastName: false }));
                      }}
                    />
                    {emojiIn.lastName && (
                      <p
                        className="f__err"
                        id="login-last-name-err"
                        role="alert"
                      >
                        {t("EMOJI_NOT_ALLOWED", NO_EMOJI)}
                      </p>
                    )}
                  </div>
                  {signupTypo && offeredTypos.length > 0 && (
                    <div className="f f--wide" data-cfg="loginSignup">
                      <label htmlFor="login-typo">{typologyWord}</label>
                      <select
                        id="login-typo"
                        name="typoId"
                        required
                        value={typoId}
                        onChange={(e) => setTypoId(e.target.value)}
                      >
                        <option value="">—</option>
                        {offeredTypos.map((typo) => (
                          <option key={typo.id} value={String(typo.id)}>
                            {typo.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              {!asking &&
                stage === "refused" &&
                (unknownHtml.trim() !== "" ? (
                  <Prose
                    html={unknownHtml}
                    className="se-prose f--wide"
                    role="alert"
                    data-cfg="loginSignup"
                  />
                ) : (
                  <p
                    className="f--wide loginerr"
                    role="alert"
                    data-cfg="loginSignup"
                  >
                    {t("ACCOUNT_NOT_FOUND", "Ce compte n'existe pas.")}
                  </p>
                ))}

              {/* `role="alert"` so it is ANNOUNCED, not merely displayed: a screen
            reader user who submits and hears nothing has no idea it failed. */}
              {error && (
                <p className="f--wide loginerr" role="alert">
                  {error}
                </p>
              )}

              {/**
               * ⚠️ ON NE DIT PAS SI L'ADRESSE EST CONNUE. Répondre « cette adresse n'existe
               * pas » transforme ce formulaire en annuaire : on saurait, sans compte, qui est
               * inscrit à l'événement. La phrase est donc la même dans les deux cas.
               */}
              {sent === "sent" && (
                <p className="f--wide hint" role="status">
                  {t(
                    "PASSWORD_RESET_SENT",
                    "Si cette adresse est connue, un email vient de partir. Il contient de quoi vous reconnecter.",
                  )}
                </p>
              )}

              {/**
               * ⚠️ ET ON NE PROMET PAS UN COURRIER QUI NE PARTIRA JAMAIS. Le serveur répond
               * `skipped` quand l'événement n'a AUCUN modèle pour cette action : il n'y a
               * alors rien à attendre, et faire patienter devant une boîte aux lettres vide
               * est pire que de le dire.
               */}
              {sent === "no-template" && (
                <p className="f--wide hint" role="status">
                  {t(
                    "PASSWORD_RESET_UNAVAILABLE",
                    "Cet événement n'envoie pas d'email de reconnexion. Rapprochez-vous de son organisateur.",
                  )}
                </p>
              )}

              {/* Mode code : ce qu'il faut savoir avant d'agir, en `status` (rien n'a échoué). */}
              {otpMode && otpNotice === "sent" && (
                <p className="f--wide hint" role="status">
                  {t(
                    "OTP_SENT",
                    "Si cette adresse est valide, le code arrive dans un instant. Vérifiez aussi vos emails indésirables.",
                  )}
                </p>
              )}
              {otpMode && otpNotice === "resent" && (
                <p className="f--wide hint" role="status">
                  {t(
                    "OTP_RESENT",
                    "Un nouveau code vient de partir ; il remplace le précédent.",
                  )}
                </p>
              )}
              {otpMode && otpNotice === "expired" && (
                <p className="f--wide hint" role="status">
                  {t(
                    "OTP_CODE_EXPIRED",
                    "Ce code n'est plus valable : il a expiré ou a été essayé trop de fois. Demandez-en un nouveau.",
                  )}
                </p>
              )}
              {otpMode && otpNotice === "no-template" && (
                <p className="f--wide hint" role="status">
                  {t(
                    "OTP_UNAVAILABLE",
                    "Cet événement n'envoie pas de code de connexion. Rapprochez-vous de son organisateur.",
                  )}
                </p>
              )}

              <div className="f--wide">
                <Button
                  type="submit"
                  data-cfg="loginButton"
                  loading={busy}
                  disabled={
                    (asking ||
                      (stage === "known" &&
                        (linkOnly || (otpMode && !codeStage)))) &&
                    cooldown > 0
                  }
                >
                  {asking
                    ? cooldown > 0
                      ? t("RESEND_WAIT", "Renvoyer dans $1 s", [
                          String(cooldown),
                        ])
                      : t("PASSWORD_RESET_SUBMIT", "Envoyer")
                    : otpMode && codeStage
                      ? t("OTP_VERIFY", "Valider le code")
                      : stage === "email" || stage === "refused"
                        ? t("CONTINUE", "Continuer")
                        : stage === "signup"
                          ? t("CREATE_MY_ACCOUNT", "Créer mon compte")
                          : otpMode
                            ? cooldown > 0
                              ? t("RESEND_WAIT", "Renvoyer dans $1 s", [
                                  String(cooldown),
                                ])
                              : t("OTP_REQUEST", "Recevoir un code")
                            : linkOnly
                              ? cooldown > 0
                                ? t("RESEND_WAIT", "Renvoyer dans $1 s", [
                                    String(cooldown),
                                  ])
                                : t("PASSWORD_RESET_SUBMIT", "Envoyer")
                              : submitLabel}
                </Button>
              </div>

              {/* Mode code, second temps : redemander (la seule issue d'un code périmé) et
            corriger l'adresse (une faute de frappe, et le code n'arrivera jamais). */}
              {otpMode && codeStage && (
                <div className="f--wide acts">
                  {/* Pendant le délai, un texte et non un lien grisé : rien à cliquer. */}
                  {cooldown > 0 ? (
                    <span className="acts__wait" role="status">
                      {t("OTP_RESEND_WAIT", "Nouveau code possible dans $1 s", [
                        String(cooldown),
                      ])}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="lnk"
                      disabled={busy}
                      onClick={() => void askForCode(true)}
                    >
                      {t("OTP_RESEND", "Recevoir un nouveau code")}
                    </button>
                  )}
                  <button
                    type="button"
                    className="lnk"
                    disabled={busy}
                    onClick={changeAddress}
                  >
                    {t("OTP_CHANGE_EMAIL", "Changer d'adresse")}
                  </button>
                </div>
              )}

              <div className="f--wide">
                {asking ? (
                  <button type="button" className="lnk" onClick={backToSignIn}>
                    {t("BACK_TO_SIGN_IN", "Revenir à la connexion")}
                  </button>
                ) : (
                  forgotShown && (
                    <button
                      type="button"
                      className="lnk"
                      data-cfg="loginForgot"
                      onClick={openForgot}
                    >
                      {forgotLabel}
                    </button>
                  )
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
